import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import "./work.scss";

import { FaGithub, FaExternalLinkAlt, FaFolderOpen } from "react-icons/fa";
import {
  SiReact,
  SiNodedotjs,
  SiPostgresql,
  SiSpringboot,
  SiMysql,
  SiPython,
  SiPandas,
  SiNumpy,
  SiTensorflow,
  SiOpencv,
} from "react-icons/si";

import EvernorthHealthInc from "../../assets/Evernorth-Health-Inc.png";
import EBookDBMS from "../../assets/EBook-DBMS.png";
import SmartHealthHub from "../../assets/smart-heathcare-hub.png";
import BipartiteMatching from "../../assets/Bipartite.png";
import FakeCurrencyImg from "../../assets/FakeCurrencyImg.png";

const projects = [
  {
    title: "Smart HealthCare Hub",
    description:
      "A healthcare management platform connecting patients, providers, administrators, and pharmacists with secure access and streamlined workflows.",
    tools: [
      { icon: <SiReact />, label: "React" },
      { icon: <SiSpringboot />, label: "Spring Boot" },
      { icon: <SiPostgresql />, label: "PostgreSQL" },
    ],
    image: SmartHealthHub,
    links: [
      { type: "live", label: "Live", href: "https://sxl9752.uta.cloud/" },
    ],
  },
  {
    title: "Evernorth",
    description:
      "A healthcare technology platform supporting secure member experiences, benefits workflows, and scalable backend integrations—built with production-ready UI and reliability-focused engineering.",
    tools: [
      { icon: <SiReact />, label: "React" },
      { icon: <SiNodedotjs />, label: "Node.js" },
      { icon: <SiPostgresql />, label: "PostgreSQL" },
    ],
    image: EvernorthHealthInc,
    links: [
      { type: "live", label: "Live", href: "https://www.evernorth.com/" },
    ],
  },
  {
    title: "E-Book Database System",
    description:
      "A relational database system for an e-book platform managing customers, books, authors, publishers, and best-seller tracking—built with a normalized schema and SQL scripts for creation, loading, and analytics.",
    tools: [
      { icon: <SiMysql />, label: "SQL" },
      { icon: <SiPostgresql />, label: "RDBMS" },
    ],
    image: EBookDBMS,
    links: [
      {
        type: "code",
        label: "Source Code",
        href: "https://github.com/sirishalokanath/EBookDBMS",
      },
    ],
  },
  {
    title: "Fake Currency Note Detection",
    description:
      "Built a Python desktop app to classify currency notes as Real vs Fake using a CNN (TensorFlow/TFLearn), with OpenCV preprocessing and fast inference using saved checkpoints.",
    tools: [
      { icon: <SiPython />, label: "Python" },
      { icon: <SiTensorflow />, label: "TensorFlow/TFLearn" },
      { icon: <SiOpencv />, label: "OpenCV" },
    ],
    image: FakeCurrencyImg,
    links: [
      {
        type: "code",
        label: "Source Code",
        href: "https://github.com/sirishalokanath/Indian-Currency-Recognition-and-Fake-Detection-System-",
      },
    ],
  },
  {
    title: "Bipartite Matching",
    description:
      "A Python GUI app comparing online (dynamic) vs offline (static) bipartite matching to recommend best-paying customers, track top suppliers, total sales, and max profit using an e-commerce dataset.",
    tools: [
      { icon: <SiPython />, label: "Python" },
      { icon: <SiPandas />, label: "Pandas" },
      { icon: <SiNumpy />, label: "NumPy" },
    ],
    image: BipartiteMatching,
    links: [
      {
        type: "code",
        label: "Source Code",
        href: "https://github.com/sirishalokanath/BipartiteMatch",
      },
    ],
  },
];

const LinkPill = ({ type, href, label }) => {
  const icon = type === "code" ? <FaGithub /> : <FaExternalLinkAlt />;
  return (
    <a className="workLink" href={href} target="_blank" rel="noreferrer">
      <span className="workLink__icon">{icon}</span>
      <span>{label}</span>
    </a>
  );
};

export default function Work() {
  const [active, setActive] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  const lockRef = useRef(false);
  const startYRef = useRef(null);
  const stackRef = useRef(null);

  // ✅ Responsive mode switch
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 980px)");
    const handler = () => setIsMobile(mq.matches);

    handler();
    if (mq.addEventListener) mq.addEventListener("change", handler);
    else mq.addListener(handler);

    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", handler);
      else mq.removeListener(handler);
    };
  }, []);

  const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

  const go = useCallback((dir) => {
    if (lockRef.current) return;
    lockRef.current = true;

    setActive((prev) => clamp(prev + dir, 0, projects.length - 1));

    window.setTimeout(() => {
      lockRef.current = false;
    }, 520);
  }, []);

  const onWheel = useCallback(
    (e) => {
      // ✅ Desktop only
      if (isMobile) return;
      e.preventDefault();
      const dy = e.deltaY;
      if (Math.abs(dy) < 18) return;
      if (dy > 0) go(1);
      else go(-1);
    },
    [go, isMobile]
  );

  const onTouchStart = (e) => {
    // ✅ Desktop/tablet swipe only; mobile should scroll normally
    if (isMobile) return;
    if (!e.touches?.length) return;
    startYRef.current = e.touches[0].clientY;
  };

  const onTouchMove = (e) => {
    if (isMobile) return;
    if (startYRef.current != null) e.preventDefault();
  };

  const onTouchEnd = (e) => {
    if (isMobile) return;
    const startY = startYRef.current;
    startYRef.current = null;
    if (startY == null) return;

    const endY = e.changedTouches?.[0]?.clientY;
    if (endY == null) return;

    const diff = startY - endY;
    if (Math.abs(diff) < 60) return;

    if (diff > 0) go(1);
    else go(-1);
  };

  useEffect(() => {
    const el = stackRef.current;
    if (!el) return;

    if (!isMobile) {
      el.addEventListener("wheel", onWheel, { passive: false });
      return () => el.removeEventListener("wheel", onWheel);
    }
  }, [onWheel, isMobile]);

  useEffect(() => {
    const onKey = (e) => {
      if (isMobile) return;
      if (e.key === "ArrowDown" || e.key === "PageDown") go(1);
      if (e.key === "ArrowUp" || e.key === "PageUp") go(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go, isMobile]);

  const content = useMemo(() => {
    if (isMobile) {
      return (
        <div className="work__list">
          {projects.map((p, idx) => (
            <article className="proj proj--list" key={`${p.title}-${idx}`}>
              <div className="proj__content">
                <h3 className="proj__name">{p.title}</h3>
                <p className="proj__desc">{p.description}</p>

                <div className="proj__tools">
                  {p.tools.map((t, i) => (
                    <span
                      className="toolPill"
                      key={`${t.label}-${i}`}
                      title={t.label}
                    >
                      <span className="toolPill__icon">{t.icon}</span>
                      <span className="toolPill__text">{t.label}</span>
                    </span>
                  ))}
                </div>

                {p.links?.length ? (
                  <div className="proj__links">
                    {p.links.map((l) => (
                      <LinkPill
                        key={l.label}
                        type={l.type}
                        href={l.href}
                        label={l.label}
                      />
                    ))}
                  </div>
                ) : null}
              </div>

              <div className="proj__media">
                <div className="proj__imageWrap">
                  <img src={p.image} alt={p.title} />
                </div>
              </div>
            </article>
          ))}
        </div>
      );
    }

    return (
      <div
        className="work__stack"
        aria-label="Projects swipe stack"
        ref={stackRef}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {projects.map((p, idx) => (
          <article
            className={`proj proj--swipe ${idx === active ? "is-active" : ""}`}
            key={`${p.title}-${idx}`}
            style={{
              transform: `translateY(${(idx - active) * 112}%)`,
              pointerEvents: idx === active ? "auto" : "none",
            }}
          >
            <div className="proj__content">
              <h3 className="proj__name">{p.title}</h3>
              <p className="proj__desc">{p.description}</p>

              <div className="proj__tools">
                {p.tools.map((t, i) => (
                  <span
                    className="toolPill"
                    key={`${t.label}-${i}`}
                    title={t.label}
                  >
                    <span className="toolPill__icon">{t.icon}</span>
                    <span className="toolPill__text">{t.label}</span>
                  </span>
                ))}
              </div>

              {p.links?.length ? (
                <div className="proj__links">
                  {p.links.map((l) => (
                    <LinkPill
                      key={l.label}
                      type={l.type}
                      href={l.href}
                      label={l.label}
                    />
                  ))}
                </div>
              ) : null}
            </div>

            <div className="proj__media">
              <div className="proj__imageWrap">
                <img src={p.image} alt={p.title} />
              </div>
            </div>
          </article>
        ))}
      </div>
    );
  }, [active, isMobile]);

  return (
    <section id="Work" className="work">
      <div className="work__inner">
        <div className="work__titleRow">
          <div className="work__icon" aria-hidden="true">
            <FaFolderOpen />
          </div>
          <h2 className="work__title">Projects</h2>
          <div className="work__rule" aria-hidden="true" />
        </div>

        <div className="work__frame">{content}</div>
      </div>
    </section>
  );
}
